import { useRef, useState } from 'react';
import { Dialog } from './dialogs/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useApi } from '../providers/api-provider';
import { Themes } from '../general/config';
import { applyUserPreferences, browserUserPreferences } from '../general/user-preferences';

export function UserSettingsDialog({ onHide, onSaved }: { onHide: () => void; onSaved: () => void }) {
    const api = useApi();
    const [initial] = useState(browserUserPreferences);
    const [draft, setDraft] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const themeDropdown = useRef<Dropdown>(null);
    const showSelectedTheme = () => {
        const panel = themeDropdown.current?.getOverlay();
        const list = panel?.querySelector<HTMLElement>('.p-dropdown-items-wrapper');
        const selected = panel?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
        if (!list || !selected) return;
        const bounds = list.getBoundingClientRect();
        const item = selected.getBoundingClientRect();
        // Scroll only the list, keeping the dialog/page in place.
        list.scrollTop += item.top - bounds.top - (list.clientHeight - item.height) / 2;
    };
    const save = async () => {
        if (saving) return;
        setSaving(true); setError('');
        try {
            const patch = { ...(draft.theme !== initial.theme ? { theme: draft.theme } : {}),
                ...(draft.wordSuggestions !== initial.wordSuggestions ? { wordSuggestions: draft.wordSuggestions } : {}) };
            if (!Object.keys(patch).length) { onHide(); return; }
            const saved = await api.saveMyPreferences(patch);
            applyUserPreferences(saved);
            onSaved(); onHide();
        } catch { setError('Your settings could not be saved. Please try again.'); }
        finally { setSaving(false); }
    };
    return <Dialog header="Settings" visible onHide={() => { if (!saving) onHide(); }} modal draggable={false}
        style={{ width: '30rem', maxWidth: 'calc(100vw - 2rem)' }} closable={!saving}
        footer={<><Button label="Cancel" text disabled={saving} onClick={onHide}/><Button label="Save" icon="pi pi-check" loading={saving} onClick={save}/></>}>
        <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-2"><label htmlFor="settings-theme" className="font-semibold">Appearance</label>
                <Dropdown ref={themeDropdown} inputId="settings-theme" value={draft.theme} options={Themes} filter resetFilterOnHide
                    onShow={showSelectedTheme} className="w-full" scrollHeight="min(28rem, 60vh)"
                    onChange={event => setDraft(current => ({ ...current, theme: event.value }))} disabled={saving}/>
                <small className="text-color-secondary">Choose a light or dark theme. Your choice applies after saving.</small></div>
            <div><div className="flex align-items-center gap-2">
                <Checkbox inputId="settings-word-suggestions" checked={draft.wordSuggestions} disabled={saving}
                    onChange={event => setDraft(current => ({ ...current, wordSuggestions: !!event.checked }))}/>
                <label htmlFor="settings-word-suggestions">Word suggestions</label></div>
                <p className="text-sm text-color-secondary mb-0">Available in supported text fields. Some learning exercises disable suggestions.</p></div>
            {error && <p role="alert" className="text-red-500 m-0">{error}</p>}
        </div>
    </Dialog>;
}
